// Tiny formatters used across the app — kept here so screen-level
// components stay free of inline nested ternaries.

import {
  isEarlyBirdActive,
  isPaidPricing,
  resolveEffectivePricing,
} from "./pricing";

/** ISO 4217 code → display symbol via Intl (e.g. EUR → €). */
export function currencySymbol(currency) {
  const code = (currency || "").trim().toUpperCase();
  if (!code) return "";
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value || ` ${code}`;
  } catch {
    return ` ${code}`;
  }
}

/** Structured parts for rendering amount large / currency small in UI pills. */
export function pricingPillParts(pricing, { rsvpCount } = {}) {
  if (!pricing || pricing.type === "free") {
    return { type: "free", label: "FREE" };
  }
  const resolved = resolveEffectivePricing(pricing, { rsvpCount: rsvpCount ?? 0 });
  const currency = currencySymbol(resolved.currency).trim();
  if (resolved.early_bird_active) {
    return {
      type: "early",
      prefix: "EARLY",
      amount: String(resolved.early_bird_amount),
      currency,
    };
  }
  return {
    type: "paid",
    amount: String(resolved.amount),
    currency,
  };
}

/** Structured parts for the event hero pricing pill. */
export function pricingHeroParts(pricing, { rsvpCount } = {}) {
  if (!pricing || pricing.type === "free") {
    return { type: "free", label: "FREE" };
  }
  const resolved = resolveEffectivePricing(pricing, { rsvpCount: rsvpCount ?? 0 });
  const currency = currencySymbol(resolved.currency).trim();
  if (resolved.early_bird_active) {
    return {
      type: "early-combo",
      prefix: "EARLY-BIRD",
      earlyAmount: String(resolved.early_bird_amount),
      regularAmount: String(resolved.amount),
      currency,
    };
  }
  if (isPaidPricing(resolved) && resolved.type === "paid-self-managed") {
    return {
      type: "self-managed",
      prefix: "PAY",
      amount: String(resolved.amount),
      currency,
      suffix: "· self-managed",
    };
  }
  return {
    type: "paid",
    amount: String(resolved.amount),
    currency,
  };
}

function formatAmountCurrency(amount, sym) {
  const currency = String(sym).trim();
  return currency ? `${amount} ${currency}` : String(amount);
}

/**
 * Returns the short pricing label shown on an event card / pill.
 * Examples: "FREE", "EARLY 8€", "12€"
 */
export function pricingPillLabel(pricing, { rsvpCount } = {}) {
  if (!pricing || pricing.type === "free") return "FREE";
  const resolved = resolveEffectivePricing(pricing, { rsvpCount: rsvpCount ?? 0 });
  const sym = currencySymbol(resolved.currency);
  if (resolved.early_bird_active) {
    return `EARLY ${formatAmountCurrency(resolved.early_bird_amount, sym)}`;
  }
  return formatAmountCurrency(resolved.amount, sym);
}

/**
 * The amount owed right now, plus context when an early-bird discount applies.
 * Used where the user is actually paying (not just browsing the price).
 * Example: { label: "1 ₹", note: "Early-bird price — 2 ₹ after the offer ends" }
 */
export function pricingDueParts(pricing, { rsvpCount } = {}) {
  if (!pricing || pricing.type === "free") return { label: "FREE", note: null };
  const resolved = resolveEffectivePricing(pricing, { rsvpCount: rsvpCount ?? 0 });
  const sym = currencySymbol(resolved.currency);
  const amount = resolved.effective_amount ?? resolved.amount;
  const label = formatAmountCurrency(amount, sym);
  if (resolved.early_bird_active) {
    return {
      label,
      note: `Early-bird price — ${formatAmountCurrency(resolved.amount, sym)} after the offer ends`,
    };
  }
  return { label, note: null };
}

/** Compact terms line for cards, e.g. "Until 12 Feb · 5 spots left". */
export function earlyBirdTermsLabel(pricing, { rsvpCount } = {}) {
  const resolved = resolveEffectivePricing(pricing, { rsvpCount: rsvpCount ?? 0 });
  if (!resolved.early_bird_active) return null;

  const parts = [];
  if (resolved.early_bird_cutoff) {
    try {
      const d = new Date(resolved.early_bird_cutoff);
      if (!Number.isNaN(d.getTime())) {
        parts.push(
          `Until ${d.toLocaleString("en-GB", { day: "numeric", month: "short" })}`
        );
      }
    } catch {
      /* ignore */
    }
  }
  if (resolved.early_bird_spots_left != null) {
    parts.push(`${resolved.early_bird_spots_left} spots left`);
  }
  return parts.length ? parts.join(" · ") : null;
}

/** Avatar background for a role. */
export function roleAvatarBg(role) {
  if (role === "Leader") return "bg-leaders text-plum";
  if (role === "Follower") return "bg-followers text-plum";
  return "bg-cream text-plum";
}

export { coverBackgroundStyle, getCoverGradient } from "./coverGradients";

/** Google Maps search query from venue + city. */
export function eventMapQuery({ location, city }) {
  const venue = (location || "").trim();
  if (!venue) return "";
  const c = (city || "").trim();
  if (!c || venue.toLowerCase().includes(c.toLowerCase())) return venue;
  return `${venue}, ${c}`;
}

/** Custom map link, or a Google Maps search for venue + city when empty. */
export function eventMapLink({ location, city, location_map_link }) {
  const custom = (location_map_link || "").trim();
  if (custom) return custom;
  const query = eventMapQuery({ location, city });
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
